
import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef, inject} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import {accessEReportingRolesHelpers} from '../../../datas/accessEReportingRoles.helpers';
import {ROLES, masqueDepensesEbusiness, peutGererOperations, userJV, usersFacturesLectureSeule, usersVieira} from '../../../datas/roles.helpers';
import {environment} from '../../../../../environments/environment';
import {MenuItem} from 'primeng/api';
import {Router, RouterModule} from '@angular/router';
import {LoopBackAuth} from '../../../../shared/sdk';
import {AppSessionService} from '../../../services/app.session.service';
import {LayoutService} from '../../../services/layout.service';
import {AppGlobalEventManagerService} from '../../../services/app.globalEventManager.service';
import {apiParameterInterface} from '../../../interfaces/apiParameter.interface';
import {TranslocoPipe} from '@jsverse/transloco';
import {Badge} from 'primeng/badge';
import {Menu} from 'primeng/menu';
import {CommonModule} from '@angular/common';
import {PeopleWithrightsInterface} from '../../../interfaces/peopleWithrights.interface';
import {Tooltip} from 'primeng/tooltip';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { TenantService } from '../../../services/tenant.service';
import { blocEbusiness, pageAutorisee, pageIdDepuisLien } from '../../../datas/ebusiness.pages';

/**
 * Construction PURE des items du menu (extraite de ngOnInit pour être testable
 * sans DOM ni TestBed) : mêmes libellés, mêmes routes, mêmes règles `visible`.
 *
 * Les dépendances « ambiantes » (environment, ROLES, constantes utilisateurs)
 * restent des imports de module : elles sont figées au build et identiques en
 * test. Ce qui varie par session (user, apiParameters) ou par tenant (labels,
 * terminologie, activation des modules) est passé en paramètre.
 *
 * Pilotage tenant (SP1) :
 * - libellés de modules via `tenant.moduleLabel()` (identité en VGE) ;
 * - terminologie métier (« Sinistres »…) via `tenant.term()` (identité en VGE) ;
 * - `visible` : le flag historique `environment.Modules.X` reste l'interrupteur
 *   global, le tenant ajoute son VETO via `tenant.moduleEnabled('X')`.
 * NB : les libellés passant aussi par `|transloco` dans le template, un label
 * remappé ici ne doit PAS avoir de clé équivalente dans les overlays
 * `xx.vgi.json` / `xx.vgs.json` (un seul canal — cf. « Anomalies Sinistres »).
 *
 * ⚠ Non-régression VGE : la sortie pour un user admin est verrouillée par la
 * fixture `menu-partial.vge.fixture.json` (égalité PROFONDE dans
 * `menu-partial.tenant.spec.ts`). Toute modification de contenu doit
 * assumer explicitement la mise à jour de cette fixture.
 */
export function buildMenuItems(
  user: PeopleWithrightsInterface,
  apiParameters: apiParameterInterface | undefined,
  tenant: TenantService,
): MenuItem[] {
  // Drapeau porté par la personne (roles.helpers), plus une liste d'identifiants
  // à maintenir dans le code : un mouvement d'équipe se règle en fiche.
  const eBusinessblockDepenses = masqueDepensesEbusiness(user as any);

  return masquerPagesEbusinessFermees([
    {
      label: 'General',
      items: [
        {label: 'Accueil', icon: 'fal fa-grid-2 text-xl', routerLink: ['/dashboard']},
        {label: 'Mes actifs immobiliers', icon: 'fa fa-light fa-buildings text-xl', routerLink: ['/mes-actifs-immobiliers']},
        {label: 'Outil ' + tenant.moduleLabel('DOnet'), icon: 'fab fa-dochub text-xl', routerLink: ['/actifs'],
          visible: environment.Modules.DOnet && tenant.moduleEnabled('DOnet'),
          items: [
            {label: 'Opérations ' + tenant.moduleLabel('DOnet'), icon: 'fa-light fa-list-dropdown text-xl', routerLink: ['/DOnet/operations']},

            {label: 'Outil DOstat', icon: 'fa-light fa-chart-bar text-xl', routerLink: ['/DOnet/DOstat'], visible: ROLES.admin.indexOf(user.profil.name) > -1 && tenant.moduleEnabled('DOstat')},
            {label: 'Synthèse analytique', icon: 'fa-light fa-chart-candlestick text-xl', routerLink: ['/DOnet/DOstat/analytics'], visible: user.accessEbuiz === 1 && tenant.moduleEnabled('DOstat')},
            {label: 'Reporting durée dossiers', icon: 'fa-light fa-stopwatch text-xl', routerLink: ['/DOnet/DOstat/duree-reporting'], visible: user.accessEbuiz === 1 && tenant.moduleEnabled('DOstat')},
            // Terminologie tenant côté TS (term()) — la clé « Anomalies
            // Sinistres » a été retirée des overlays transloco vgi/vgs pour
            // éviter un double canal de traduction.
            {label: 'Anomalies ' + tenant.term('Sinistres'), icon: 'fa-light fa-bugs text-xl', routerLink: ['/DOnet/DOstat/anomalies'], visible: user.accessEbuiz === 1 && tenant.moduleEnabled('DOstat')},
            {label: 'Anomalies Opérations', icon: 'fa-light fa-folder-tree text-xl', routerLink: ['/DOnet/DOstat/anomalies-operations'], visible: user.accessEbuiz === 1 && tenant.moduleEnabled('DOstat')},
            {label: 'Rappels auto', icon: 'fa-light fa-bell-exclamation text-xl', routerLink: ['/Admin/rappels-auto-sinistres'], visible: ROLES.admin.indexOf(user.profil.name) > -1},
            {label: 'E-Reporting', icon: 'fa-light fa-upload text-xl', routerLink: ['/DOnet/e-reporting']},
            {label: 'Aide à la gestion', icon: 'fa-light fa-eye-evil text-xl', routerLink: ['/DOnet/operations-global-collaborateurs'], visible: ROLES.admin.indexOf(user.profil.name) > -1},
          ]
        },
        {label: 'Outil ' + tenant.moduleLabel('MULTInet'), icon: 'fa-brands fa-monero text-xl', routerLink: ['/actifs'],
          // Historiquement sans `visible` : le spread conditionnel n'ajoute la
          // clé (veto tenant) que si le module est fermé, pour rester
          // strictement identique à la fixture VGE (clé absente = visible).
          ...(environment.Modules.MULTInet && tenant.moduleEnabled('MULTInet') ? {} : {visible: false}),
          items: [
            {label: 'Opérations ' + tenant.moduleLabel('MULTInet'), icon: 'fa-light fa-list-dropdown text-xl', routerLink: ['/MULTInet/operations']},
            {label: 'Outil MULTIstat', icon: 'fa-light fa-chart-bar text-xl', routerLink: ['/MULTInet/MULTIstat'], visible: ROLES.admin.indexOf(user.profil.name) > -1},
            {label: 'Synthèse analytique', icon: 'fa-light fa-chart-candlestick text-xl', routerLink: ['/MULTInet/MULTIstat/analytics'], visible: user.accessEbuiz === 1},
            {label: 'Anomalies', icon: 'fa-light fa-bugs text-xl', routerLink: ['/MULTInet/MULTIstat/anomalies'], visible: user.accessEbuiz === 1},
            {label: 'E-Reporting', icon: 'fa-light fa-upload text-xl', routerLink: ['/MULTInet/e-reporting']},
          ]
        },
        {label: 'Outil ' + tenant.moduleLabel('GPAnet'), icon: 'fab fa-gofore text-xl', routerLink: ['/GPAnet'], visible: environment.Modules.GPAnet && tenant.moduleEnabled('GPAnet')},
        {label: 'Outil ' + tenant.moduleLabel('ULTRAnet'), icon: 'fa-brands fa-uniregistry text-xl', routerLink: ['/ULTRAnet/home'],
          visible: ROLES.ultranet.indexOf(user.profil.name) > -1 && environment.Modules.ULTRAnet && tenant.moduleEnabled('ULTRAnet'),
          items: [
            {label: 'Dashboard', icon: 'fa-light fa-house text-xl', routerLink: ['/ULTRAnet/home']},
            {label: 'Audits', icon: 'fa-light fa-chart-bar text-xl', routerLink: ['/ULTRAnet/audits']},
            {label: 'Expertises', icon: 'fa-light fa-chart-bar text-xl', routerLink: ['/ULTRAnet/expertises']},
            {label: 'Calendrier RDV', icon: 'fa-light fa-calendar-days text-xl', routerLink: ['/ULTRAnet/calendrier']},
            {label: 'Alerte délais', icon: 'fa-light fa-calendar-exclamation text-xl', routerLink: ['/ULTRAnet/alerte-delais']},
            {label: 'Import XLSX', icon: 'fa-light fa-file-import text-xl', routerLink: ['/ULTRAnet/import']},
            {label: 'Chronos', icon: 'fa-light fa-clock text-xl', routerLink: ['/ULTRAnet/chronos'], visible: tenant.moduleEnabled('Chronos')},
          ]
        },
      ]
    },
    {
      label: 'Prestataires',
      visible: (environment.Presta.Universign || environment.Presta.AR24) && ROLES.admin.indexOf(user.profil.name) > -1 && tenant.moduleEnabled('Prestataires'),
      items: [
        {label: 'Universign', icon: 'fa-light fa-file-signature text-xl', routerLink: ['/Universign', 'list'], visible: environment.Presta.Universign && ROLES.admin.indexOf(user.profil.name) > -1},
        {label: 'AR24', icon: 'fa-light fa-envelope text-xl', routerLink: ['/AR24', 'list'], visible: environment.Presta.AR24 && ROLES.admin.indexOf(user.profil.name) > -1},
        // {label: 'SmileMood', icon: 'fa-light fa-check-to-slot text-xl', routerLink: ['/SmileMood', 'list'], visible: usersVieira.indexOf(user.id) > -1},
      ]
    },
    {
      label: 'Organisation',
      visible: ROLES.admin.indexOf(user.profil.name) > -1,
      items: [
        {label: tenant.moduleLabel('eBoarding'), icon: 'fa-light fa-chalkboard text-xl', visible: ROLES.admin.indexOf(user.profil.name) > -1 && tenant.moduleEnabled('eBoarding'),
          items: [

            {label: 'Dashboard', icon: 'fa-light fa-house text-xl', routerLink: ['/eBoarding/home']},
            {label: 'Répartition des affaires', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBoarding/repartition-operations']},
            // {label: 'Evénements', icon: 'fa-light fa-history text-xl', routerLink: ['/eBoarding/events']},
            // {label: 'Chats interne VGE', icon: 'fa-light fa-comments text-xl', routerLink: ['/eBoarding/chat']},
            // {label: 'Chats clients', icon: 'fa-light fa-headset text-xl', routerLink: ['/eBoarding/chat-public']},
            {label: 'Alerte délais', icon: 'fa-light fa-calendar text-xl', routerLink: ['/eBoarding/alerte-delais']},
          ]
        },
        {label: tenant.moduleLabel('eBusiness'), icon: 'fa-light fa-cash-register text-xl', routerLink: ['/actifs'], visible: ROLES.admin.indexOf(user.profil.name) > -1 && tenant.moduleEnabled('eBusiness'),
          items: [
            {label: 'Dashboard', icon: 'fa-light fa-house text-xl', routerLink: ['/eBusiness/home'], visible: user.accessEbuiz === 1},
            {label: 'Historique Dashboard', icon: 'fa-light fa-chart-line text-xl', routerLink: ['/eBusiness/dashboard-history'], visible: user.accessEbuiz === 1},
            {label: 'Prospect / Mission', icon: 'fa-light fa-cash-register text-xl',
              items: [
                {label: 'Contrat Prospect', icon: 'fa-light fa-bags-shopping text-xl', routerLink: ['/eBusiness/contratsProspect']},
                {label: 'Contrat Signé', icon: 'fa-light fa-money-check-pen text-xl', routerLink: ['/eBusiness/contrats']},
                {label: 'Prév. prospect', icon: 'fa-light fa-money-check-edit-alt text-xl', routerLink: ['/eBusiness/prevProspects']},
                {label: 'Budget coûts', icon: 'fa-light fa-money-check-edit-alt text-xl', routerLink: ['/eBusiness/budgetCouts']},
              ],
              visible: (user.accessEbuiz === 1)
            },
            {label: 'Prévisionnel Facturation', icon: 'fa-light fa-file-invoice text-xl', routerLink: ['/eBusiness/factures/previsionnelFacturation'], visible: user.accessEbuiz === 1},
            {label: 'Prévisionnel Budgétaire', icon: 'fa-light fa-table text-xl', routerLink: ['/eBusiness/factures/previsionnelBudgetaire'], visible: user.accessEbuiz === 1},
            {label: 'Prévisionnel Budgétaire ', icon: 'fa-light fa-table text-xl', routerLink: ['/Admin/previsionnelBudgetaire'], visible: user.accessEbuiz !== 1},
            {label: tenant.term('Sinistres') + ' Non Facturés', icon: 'fa-light fa-triangle-exclamation text-xl', routerLink: ['/eBusiness/sinistresNonFactures'], visible: user.accessEbuiz === 1},
            {label: 'Créances annuelles', icon: 'fa-light fa-table text-xl', routerLink: ['/eBusiness/creancesAnnuelles'], visible: user.accessEbuiz === 1},
            {label: 'Créances mensuelles', icon: 'fa-light fa-file-invoice text-xl', routerLink: ['/eBusiness/creances'], visible: user.accessEbuiz === 1},
            {label: 'Temps passés', icon: 'fa-light fa-stopwatch-20 text-xl', routerLink: ['/eBusiness/timesheet'], visible: user.accessEbuiz === 1},
            {label: 'Liste Factures', icon: 'fa-light fa-file-invoice-dollar text-xl', routerLink: ['/eBusiness/factures'], visible: user.accessEbuiz === 1},
            // Miroir en consultation seule, HORS eBusiness (donc hors du masquage
            // SP4 : pageIdDepuisLien rend null pour /Admin). Demande Yah 25/08/2026.
            {label: 'Liste Factures ', icon: 'fa-light fa-file-invoice-dollar text-xl', routerLink: ['/Admin/factures'], visible: (user.accessEbuiz !== 1 && usersFacturesLectureSeule.includes(user.id))},
            // Les échéanciers n'avaient AUCUNE entrée de menu : la page ne se
            // rejoignait que par un bouton de la liste des factures, alors que Léo
            // et Yah doivent pouvoir les créer et les éditer (demande 25/08/2026).
            // Fermeture par tenant possible sans toucher au code : déclarer
            // `factures/echeanciers/list: false` dans MODULES.EBUSINESS.pages.
            {label: 'Échéanciers GPA / GBF', icon: 'fa-light fa-calendar-range text-xl', routerLink: ['/eBusiness/factures/echeanciers/list'], visible: user.accessEbuiz === 1},
            {label: 'Relances Facturation', icon: 'fa-light fa-calendar text-xl', routerLink: ['/eBusiness/alerte-delais'], visible: user.accessEbuiz === 1},
            {label: 'Plan Trésorerie', icon: 'fa-light fa-scale-balanced text-xl', routerLink: ['/eBusiness/planTresorerie'], visible: (user.accessEbuiz === 1 && !eBusinessblockDepenses)},
            {label: 'Rapprochement bancaire', icon: 'fa-light fa-building-columns text-xl', routerLink: ['/eBusiness/rapprochement-bancaire'], visible: (user.accessEbuiz === 1 && !eBusinessblockDepenses)},
            {label: 'Gestion Operations', icon: 'fa-light fa-building-shield text-xl', routerLink: ['/eBusiness/operations'], visible: user.accessEbuiz === 1},
            {label: 'Fusion Opérations', icon: 'fa-light fa-code-merge text-xl', routerLink: ['/eBusiness/operations/fusion'], visible: user.accessEbuiz === 1},
            {label: 'Business Plan', icon: 'fa-light fa-cash-register text-xl',
              items: [
                {label: 'Business Plan Annuel', icon: 'fa-light fa-magnifying-glass-dollar text-xl', routerLink: ['/eBusiness/businessPlan']},
                {label: 'Contrat signé', icon: 'fa-light fa-file-signature text-xl', routerLink: ['/eBusiness/contratsSignes']},
                {label: 'Frais - dépense', icon: 'fa-light fa-credit-card text-xl', routerLink: ['/eBusiness/depenses']},
              ], visible: (user.accessEbuiz === 1 && !eBusinessblockDepenses)
            },
            {label: 'Reporting ' + tenant.term('Sinistres'), icon: 'fa-light fa-chart-pie text-xl', routerLink: ['/eBusiness/reportingSinistres'], visible: user.accessEbuiz === 1},
          ]
        },
        {label: 'Sociétés',icon: 'fa-light fa-address-card text-xl',routerLink: ['/Admin', 'societes'], visible: ROLES.admin.indexOf(user.profil.name) > -1},
        {label: 'Utilisateurs',icon: 'fa-light fa-users-gear text-xl',routerLink: ['/Admin', 'utilisateurs'], visible: ROLES.admin.indexOf(user.profil.name) > -1},
        {label: 'Gestion Operations', icon: 'fa-light fa-building-shield text-xl', routerLink: ['/Admin/manageoperations'], visible: peutGererOperations(user as any)},
        {label: 'Opérations Corporate',icon: 'fa-light fa-hotel text-xl',routerLink: ['/Admin', 'operations'], visible: ROLES.admin.indexOf(user.profil.name) > -1},

      ]
    },
    {
      label: 'Administration',
      visible: ROLES.admin.indexOf(user.profil.name) > -1,
      items: [
        {
          label: 'Paramètres', icon: 'fa-light fa-gear text-xl',
          visible: ROLES.admin.indexOf(user.profil.name) > -1,
          items: [
            {label: tenant.moduleLabel('GPAnet'), icon: 'fab fa-gofore text-xl',
              items: [
                {label: 'Etat des ' + tenant.term('réclamations'), icon: 'fa-light fa-list-alt text-xl', routerLink: ['/GPAnet/etats-reclamation']},
                // HealthBoard (VGS) — arborescence des catégories : entrée
                // VGS-ONLY. Spread conditionnel sur tenant.key : ABSENTE en
                // VGE/VGI (fixture VGE inchangée, aucune assertion vgi touchée).
                ...(tenant.key === 'vgs' ? [
                  {label: 'Catégories ' + tenant.moduleLabel('GPAnet'), icon: 'fa-light fa-tags text-xl', routerLink: ['/GPAnet/categories']},
                ] : []),
              ]
            },
            {label: tenant.moduleLabel('DOnet'), icon: 'fab fa-dochub text-xl',
              items: [
                {label: 'Etat des ' + tenant.term('sinistres'), icon: 'fa-light fa-list-alt text-xl', routerLink: ['/DOnet/etats-sinistre']},
                {label: 'Etat des suivis', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/DOnet/etats-suivis-sinistre']},
                {label: 'Etat des devis', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/DOnet/etats-devis']},
              ]
            },
            {label: tenant.moduleLabel('MULTInet'), icon: 'fa-brands fa-monero text-xl',
              // Veto tenant (revue Task 4) : groupe historiquement sans `visible`
              // → spread conditionnel (clé absente en VGE, identique à la fixture).
              ...(tenant.moduleEnabled('MULTInet') ? {} : {visible: false}),
              items: [
                {label: 'Etat des ' + tenant.term('sinistres'), icon: 'fa-light fa-list-alt text-xl', routerLink: ['/MULTInet/etats-sinistre']},
                {label: 'Etat des suivis', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/MULTInet/etats-suivis-sinistre']},
                {label: 'Etat des devis', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/MULTInet/etats-devis']},

              ]
            },
            {label: tenant.moduleLabel('ULTRAnet'), icon: 'fa-light fa-database text-xl',
              // Gate tenant + restriction aux rôles ULTRAnet (master 2026-07-28) combinés.
              visible: tenant.moduleEnabled('ULTRAnet') && ROLES.ultranet.indexOf(user.profil.name) > -1,
              items: [
                {label: 'Etat des audits', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/ULTRAnet/etats-audit']},
                {label: 'Etat des suivis audits', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/ULTRAnet/etats-suivis-audit']},
              ]
            },
            {label: tenant.moduleLabel('eBusiness'), icon: 'fa-light fa-cash-register text-xl',
              visible: ROLES.admin.indexOf(user.profil.name) > -1 && user.accessEbuiz === 1 && tenant.moduleEnabled('eBusiness'),
              items: [
                {label: 'indices ICC', icon: 'fa-light fa-chart-line text-xl', routerLink: ['/eBusiness/icc']},
                // {label: 'Types dépenses', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBusiness/typesdepense']},
                {label: 'Prestations', icon: 'fa-light fa-prescription text-xl', routerLink: ['/eBusiness/prestations']},
                {label: 'Types prestations', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBusiness/typesprestation']},
                {label: 'Types Audit', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBusiness/typesaudit']},
                {label: 'Etats propos. prospects', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBusiness/etats-commerciaux']},
                {label: 'Etats presta. prospects', icon: 'fa-light fa-list-alt text-xl', routerLink: ['/eBusiness/etats-prestations']},
              ]
            },
          ]
        },
        {label: 'Emails', icon: 'fa-light fa-file-lines text-xl', routerLink: ['/Admin/tests/emails'], visible: user.id === 1},
        {label: tenant.moduleLabel('DEVnet'), icon: 'fa-light fa-code-simple text-xl', routerLink: ['/DEVnet'], visible: ROLES.admin.indexOf(user.profil.name) > -1 && environment?.Modules?.DEVnet && tenant.moduleEnabled('DEVnet')},
        {label: 'Crons tasks', icon: 'fa-light fa-route text-xl', routerLink: ['/Admin/cron-logs'], visible: user.id === 1 || user.id === userJV},
        {label: 'Liens directs (mails)', icon: 'fa-light fa-link text-xl', routerLink: ['/Admin/file-links'], visible: user.id === 1 || user.id === userJV},
        {label: 'Historique dashboard', icon: 'fa-light fa-chart-line text-xl', routerLink: ['/Admin/dashboard-history'], visible: user.accessEbuiz === 1},
      ]
    },
    {
      label: 'à télécharger',
      items: [
        // Docs PDF gardées par le moduleEnabled du module concerné (spread
        // conditionnel : clé `visible` absente quand le module est actif, pour
        // rester identique à la fixture VGE).
        {label: 'Documentation GPAnet', icon: 'fa-light text-vge fa-file-pdf text-xl', url: apiParameters?.VGE?.PATHS?.file?.fullpath+'VGE - GPA - documentation utilisateur.pdf',
          ...(tenant.moduleEnabled('GPAnet') ? {} : {visible: false})},
        {label: 'Documentation DOnet', icon: 'fa-light text-red-800 fa-file-pdf text-xl', url: apiParameters?.VGE?.PATHS?.file?.fullpath+'VGE - DOnet - documentation utilisateur.pdf',
          ...(tenant.moduleEnabled('DOnet') ? {} : {visible: false})},
      ]
    },
  ], apiParameters, tenant.key, (m) => tenant.moduleEnabled(m as any));
}

/**
 * SP4 — Masque les entrées de menu pointant vers une page eBusiness fermée pour
 * le tenant courant (`MODULES.EBUSINESS.pages` / capacités, cf.
 * `core/datas/ebusiness.pages.ts`). La garde de route refuse déjà l'accès ;
 * ceci évite d'afficher un lien qui renverrait au tableau de bord.
 *
 * Post-traitement de l'arbre plutôt qu'une condition sur chacune des ~15 entrées
 * eBusiness : une entrée ajoutée plus tard est couverte sans y penser.
 *
 * ⚠ N'ÉCRIT `visible` QUE POUR FERMER. Une entrée autorisée ressort à
 * l'identique — clé absente reste absente, valeur existante inchangée. C'est
 * indispensable : la sortie VGE est verrouillée par égalité PROFONDE sur
 * `menu-partial.vge.fixture.json`, et VGE ne déclare aucune fermeture.
 */
function masquerPagesEbusinessFermees(
  items: MenuItem[],
  apiParameters: apiParameterInterface | undefined,
  tenantKey: string,
  moduleEnabled: (moduleKey: string) => boolean,
): MenuItem[] {
  const bloc = blocEbusiness(apiParameters);

  const parcourir = (liste: MenuItem[]): MenuItem[] => liste.map((item) => {
    const enfants = Array.isArray(item.items) ? parcourir(item.items) : item.items;
    const pageId = pageIdDepuisLien(item.routerLink);
    const ferme = pageId !== null && !pageAutorisee(bloc, tenantKey, pageId, moduleEnabled);

    if (!ferme && enfants === item.items) { return item; } // aucun changement → même objet
    return {
      ...item,
      ...(enfants === item.items ? {} : { items: enfants }),
      ...(ferme ? { visible: false } : {}),
    };
  });

  return parcourir(items);
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, RouterModule, Menu, Badge, TranslocoPipe, Tooltip, OverlayModule, PortalModule],
  templateUrl: './menu-partial.html',
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: '0' }),
        animate('300ms ease-in', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class MenuPartial implements OnInit {
  @Input() active = false;
  @Input() items: MenuItem[] | undefined;
  @Input() collapsed = false; // Nouveau input

  @Output() clicked = new EventEmitter();

  @ViewChild('floatingMenuTemplate', { static: true }) floatingMenuTemplate!: TemplateRef<any>;
  @ViewChild('subFloatingMenuTemplate', { static: true }) subFloatingMenuTemplate!: TemplateRef<any>;

  private overlayRef: OverlayRef | null = null;
  private subOverlayRef: OverlayRef | null = null;
  public hoveredItem: MenuItem | null = null;
  public hoveredSubItem: MenuItem | null = null;

  private isHoveringButton = false;
  private isHoveringMenu = false;
  private isHoveringSubMenu = false;

  // Injecter ViewContainerRef avec inject()
  private viewContainerRef = inject(ViewContainerRef);

  // Tenant courant (labels, terminologie, activation des modules)
  private _tenant = inject(TenantService);

  public user!: PeopleWithrightsInterface;

  public apiParameters: apiParameterInterface;

  public canAccessEreporting: boolean = true;

  constructor(
    private router: Router,
    public authService: LoopBackAuth,
    private _sessionService: AppSessionService,
    public layoutService: LayoutService,
    public _globalEventManager: AppGlobalEventManagerService,
    private overlay: Overlay
  ) {

    this.user = this.authService.getCurrentUserData()

    this.canAccessEreporting = this.user.DO && this.user.DO.some((e) => {
      return accessEReportingRolesHelpers.indexOf(e.role) > -1
    })

    this._globalEventManager.getApiParameters().subscribe((parameters:apiParameterInterface) => {
      this.apiParameters = parameters
    });

  }

  isActiveRoute(item: MenuItem): boolean {
    if (!item.routerLink) return false;

    const currentUrl = this.router.url;
    const itemUrl = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;

    return currentUrl.includes(itemUrl);
  }

  /**
   * Items filtrés (en place) en respectant la prop `visible` à TOUS les niveaux.
   *
   * PrimeNG <p-menu> rend lui-même le LABEL des sections racine (separator/header)
   * AVANT d'appeler le custom #item template, et n'écoute pas toujours `visible`
   * pour cette partie. Solution : on filtre l'arbre nous-mêmes avant de le passer
   * au composant. Une section dont `visible === false` est totalement écartée
   * (label + items), même si certains de ses enfants seraient visibles.
   *
   * ⚠ On filtre EN PLACE (mutation des `items` enfants) pour préserver les
   * références objet, sinon les mutations d'`expanded` faites par `toggleSubmenu`
   * sont perdues à chaque change-detection — résultat : les sous-menus ne
   * s'ouvrent plus. Ne pas refactorer en getter immuable sans repenser le state.
   */
  public visibleItems: MenuItem[] = [];

  private _filterVisibleInPlace(items: MenuItem[]): MenuItem[] {
    if (!items) return [];
    const kept = items.filter(i => i && i.visible !== false);
    for (const i of kept) {
      if (i.items && i.items.length > 0) {
        i.items = this._filterVisibleInPlace(i.items as MenuItem[]);
      }
    }
    return kept;
  }

  toggleSubmenu(item: MenuItem, event: Event, itemParent: MenuItem | null = null) {
    event.preventDefault();
    event.stopPropagation();

    if (item.items) {
      if (!itemParent) {
        // Premier niveau - permettre de fermer le menu même s'il est déjà ouvert
        if ((item as any).expanded) {
          // Si le menu est ouvert, le fermer
          (item as any).expanded = false;
          this.closeAllSubmenus(item);
        } else {
          // Fermer les autres menus principaux
          this.items?.forEach(menuItem => {
            if (menuItem !== item) {
              (menuItem as any).expanded = false;
              this.closeAllSubmenus(menuItem);
            }
          });
          // Ouvrir le menu cliqué
          (item as any).expanded = true;
        }
      } else {
        // Deuxième niveau ou plus - ne fermer que les menus du même niveau
        if (itemParent.items) {
          itemParent.items.forEach(siblingItem => {
            if (siblingItem !== item) {
              (siblingItem as any).expanded = false;
              // Fermer les sous-menus des siblings
              this.closeAllSubmenus(siblingItem);
            }
          });
        }

        // Toggle le sous-menu
        (item as any).expanded = !(item as any).expanded;
      }
    } else if (item.routerLink) {
      // Navigation pour les items sans sous-menu
      this.router.navigate(item.routerLink).then(() => {
        this.clicked.emit();
      });
    } else if (item.url) {
      // Item externe (ex: documentations PDF) → ouverture dans un nouvel onglet.
      window.open(item.url, '_blank', 'noopener,noreferrer');
      this.clicked.emit();
    }
  }

  private closeAllSubmenus(menuItem: MenuItem) {
    if (menuItem.items) {
      menuItem.items.forEach(subItem => {
        (subItem as any).expanded = false;
        // Récursif pour les niveaux plus profonds
        this.closeAllSubmenus(subItem);
      });
    }
  }

  get layoutServiceStates() {
    return this.layoutService.containerClasses();
  }

  onSubItemClick(subItem: MenuItem) {
    if (!subItem.items) {
      // Item externe (ex: documentations PDF) → ouverture dans un nouvel onglet.
      if (subItem.url) {
        window.open(subItem.url, '_blank', 'noopener,noreferrer');
      }
      this.hideFloatingMenu();
      this.clicked.emit();
    }
  }

  handleCollapsedItemClick(item: MenuItem) {
    if (item.items) {
      // Pour les items avec sous-menu, on peut soit :
      // 1. Ouvrir un menu flottant
      (item as any).hovered = !(item as any).hovered;
    } else if (item.routerLink) {
      // Navigation directe
      this.router.navigate(item.routerLink).then(() => {
        this.clicked.emit();
      });
    } else if (item.url) {
      // Item externe (ex: documentations PDF) → ouverture dans un nouvel onglet.
      window.open(item.url, '_blank', 'noopener,noreferrer');
      this.clicked.emit();
    }
  }

  onCollapsedItemHover(item: MenuItem, isHovered: boolean) {
    if (item.items) {
      (item as any).hovered = isHovered;
    }
  }

  showFloatingMenu(item: MenuItem, element: HTMLElement) {
    if (!item.items) return;

    this.hoveredItem = item;

    if (this.overlayRef) {
      this.overlayRef.dispose();
    }

    if (!this.floatingMenuTemplate) {
      console.error('floatingMenuTemplate not found');
      return;
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(element)
      .withPositions([
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 2  // Réduire de 8 à 2px
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: 'floating-menu-overlay'
    });

    const portal = new TemplatePortal(this.floatingMenuTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  hideFloatingMenu() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.hoveredItem = null;
  }

  showSubFloatingMenu(subItem: MenuItem, element: HTMLElement) {
    if (!subItem.items) return;

    this.hoveredSubItem = subItem;

    if (this.subOverlayRef) {
      this.subOverlayRef.dispose();
    }

    if (!this.subFloatingMenuTemplate) {
      console.error('subFloatingMenuTemplate not found');
      return;
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(element)
      .withPositions([
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 2
        }
      ]);

    this.subOverlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      panelClass: 'floating-submenu-overlay'
    });

    const portal = new TemplatePortal(this.subFloatingMenuTemplate, this.viewContainerRef);
    this.subOverlayRef.attach(portal);
  }

  hideSubFloatingMenu() {
    if (this.subOverlayRef) {
      this.subOverlayRef.dispose();
      this.subOverlayRef = null;
    }
    this.hoveredSubItem = null;
  }

  hideAllFloatingMenus() {
    this.hideFloatingMenu();
    this.hideSubFloatingMenu();
  }

  onMenuItemHover(item: MenuItem, element: HTMLElement, isEntering: boolean) {
    this.isHoveringButton = isEntering;

    if (isEntering) {
      this.showFloatingMenu(item, element);
    } else {
      setTimeout(() => {
        if (!this.isHoveringButton && !this.isHoveringMenu) {
          this.hideFloatingMenu();
        }
      }, 200);
    }
  }

  private currentHoveredSubItem: MenuItem | null = null;

  onSubMenuItemHover(subItem: MenuItem, element: HTMLElement, isEntering: boolean) {
    if (isEntering) {
      // Si on survole un nouvel élément différent, fermer l'ancien sous-menu
      if (this.currentHoveredSubItem && this.currentHoveredSubItem !== subItem) {
        this.hideSubFloatingMenu();
      }

      this.currentHoveredSubItem = subItem;

      if (subItem.items) {
        this.showSubFloatingMenu(subItem, element);
      }
    } else {
      // Ne fermer que si on quitte vraiment vers l'extérieur
      setTimeout(() => {
        if (!this.isHoveringSubMenu && this.currentHoveredSubItem === subItem) {
          this.hideSubFloatingMenu();
          this.currentHoveredSubItem = null;
        }
      }, 150);
    }
  }

  onSubFloatingMenuHover(isEntering: boolean) {
    this.isHoveringSubMenu = isEntering;

    if (!isEntering) {
      setTimeout(() => {
        if (!this.isHoveringSubMenu) {
          this.hideSubFloatingMenu();
          this.currentHoveredSubItem = null;
        }
      }, 150);
    }
  }

  onFloatingMenuHover(isEntering: boolean) {
    this.isHoveringMenu = isEntering;

    if (!isEntering) {
      // Délai plus long pour laisser le temps de passer au sous-menu
      setTimeout(() => {
        if (!this.isHoveringButton && !this.isHoveringMenu && !this.isHoveringSubMenu) {
          this.hideAllFloatingMenus();
          this.currentHoveredSubItem = null;
        }
      }, 200);
    }
  }

  private isHoveringOverlay(): boolean {
    // Logique pour détecter si on survole le menu flottant
    return false; // Simplifié pour l'exemple
  }

  ngOnInit() {

    this._sessionService.logguedAccountEmitter.subscribe((people) => {
      if(people){

        this.user = people;

        console.log(this.user.profil.name);

        // Construction extraite dans buildMenuItems() (pure, testée par
        // menu-partial.tenant.spec.ts avec fixture de non-régression VGE).
        this.items = buildMenuItems(this.user, this.apiParameters, this._tenant);

        // Filtrage en place (récursif) des items selon `visible` pour que
        // PrimeNG <p-menu> ne reçoive pas les sections/items masqués.
        // Préserve les références objet → `expanded` mutations persistent.
        this.visibleItems = this._filterVisibleInPlace(this.items);

      }
    });
  }


  protected readonly ROLES = ROLES;
  protected readonly environment = environment;
}
